import React from "react";
import { useAuth } from "hooks/Auth";
import { useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

export default function SupportPanel(props) {
    const auth = useAuth();
    const { settings } = useSelector((state) => state?.contentFlow);

    const requestClipboardWrite = (data) => {
        navigator.clipboard.writeText(data).then(() => {
            toast.success("Copied support PIN to clipboard", {
                duration: 2000,
            });
        });
    };

    return (
        <div className="flex h-full w-full flex-col">
            <div className="flex flex-grow flex-row overflow-auto p-4">
                <div className="w-full">
                    <div className="space-y-4">
                        <div>
                            <dt className="text-base font-medium text-gray-900">
                                Contact Support
                            </dt>
                            <dd className="mt-3 space-y-4 text-sm text-gray-700">
                                Contact support through our{" "}
                                <a
                                    href="https://snworks.zendesk.com/hc/en-us/requests/new"
                                    target="_blank"
                                    rel="noreferrer noopener"
                                    className="text-green-700 underline"
                                >
                                    help center
                                </a>
                                , or via email at{" "}
                                <a
                                    href="mailto:support@getsnworks.com"
                                    className="text-green-700 underline"
                                >
                                    support@getsnworks.com
                                </a>
                                . You can also check out{" "}
                                <a
                                    href="/help/index.html"
                                    target="_blank"
                                    rel="noreferrer noopener"
                                    className="text-green-700 underline"
                                >
                                    CEO's documentation
                                </a>
                                .
                            </dd>
                        </div>
                        <div>
                            <dt className="text-base font-medium text-gray-900">
                                Support Info
                            </dt>
                            <dd className="mt-3 space-y-4 text-sm text-gray-700">
                                <table className="table-auto">
                                    <tbody className="divide-y">
                                        <tr>
                                            <td className="py-1 pr-4">
                                                Support PIN:
                                            </td>
                                            <td className="flex">
                                                <pre>
                                                    {settings.env?.support_key}
                                                </pre>
                                                <button
                                                    type="button"
                                                    className="ml-2"
                                                    onClick={requestClipboardWrite.bind(
                                                        null,
                                                        settings.env
                                                            ?.support_key
                                                    )}
                                                >
                                                    <FontAwesomeIcon
                                                        className=" text-gray-800"
                                                        icon={solid("copy")}
                                                    />
                                                </button>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="py-1 pr-4">
                                                Publication ID:
                                            </td>
                                            <td>{auth.publication.uuid}</td>
                                        </tr>
                                        <tr>
                                            <td className="py-1 pr-4">
                                                Publication Code:
                                            </td>
                                            <td>
                                                {auth.publication.client_code}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="py-1 pr-4">
                                                Publication SRN:
                                            </td>
                                            <td>
                                                srn::publication:
                                                {auth.publication.uuid}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="py-1 pr-4">
                                                Site Timezone:{" "}
                                            </td>
                                            <td>{auth.publication.timezone}</td>
                                        </tr>
                                        <tr>
                                            <td className="py-1 pr-4">
                                                Your Timezone:{" "}
                                            </td>
                                            <td>
                                                {
                                                    Intl.DateTimeFormat().resolvedOptions()
                                                        .timeZone
                                                }
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="py-1 pr-4">
                                                Container Group:
                                            </td>
                                            <td>
                                                {settings.env?.pod
                                                    ? settings.env.pod
                                                    : "ECS-US-EAST-1"}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </dd>
                        </div>
                        <div>
                            <dt className="text-base font-medium text-gray-900">
                                Site Tag Sheet
                            </dt>
                            <dd className="mt-3 space-y-4 text-sm text-gray-500">
                                <p className="prose text-sm">
                                    Tag sheets are generated by SNworks for
                                    every site revision. Tag sheets help you
                                    determine content placement on your site.
                                </p>
                                <ul className="mt-4 list-inside list-disc">
                                    <li>
                                        <a
                                            href={`https://static.getsnworks.com/downloads/tag-sheets/${auth.publication.client_code}-latest.pdf`}
                                            className="text-green-700 underline"
                                        >
                                            {auth.publication.name} Tag Sheet -
                                            Latest
                                        </a>{" "}
                                        (PDF)
                                    </li>
                                </ul>
                            </dd>
                        </div>
                        <div>
                            <dt className="text-base font-medium text-gray-900">
                                SNworks Site and Authoring VPAT<sup>&copy;</sup>
                            </dt>
                            <dd className="mt-3 space-y-4 text-sm text-gray-500">
                                <p className="prose text-sm">
                                    VPATs are voluntary accessibility
                                    conformance reports that may be required by
                                    your parent institution.
                                </p>
                                <ul className="mt-4 list-inside list-disc">
                                    <li>
                                        <a
                                            href="https://static.getsnworks.com/downloads/VPAT-2.5-2025-SNworks.pdf"
                                            className="text-green-700 underline"
                                        >
                                            VPAT 2.5 - Updated Apr 2025
                                        </a>{" "}
                                        (PDF)
                                    </li>
                                    <li>
                                        <a
                                            href="https://static.getsnworks.com/downloads/VPAT-2.4-2022-SNworks.pdf"
                                            className="text-green-700 underline"
                                        >
                                            VPAT 2.4 - Updated Mar 2022
                                        </a>{" "}
                                        (PDF)
                                    </li>
                                    <li>
                                        <a
                                            href="https://static.getsnworks.com/downloads/VPAT-2.0-2020-SNworks.pdf"
                                            className="text-green-700 underline"
                                        >
                                            VPAT 2.0 - Updated Oct 2020
                                        </a>{" "}
                                        (PDF)
                                    </li>
                                </ul>
                                <p className="prose mt-4 text-sm">
                                    HECVAT is a Higher Education Community
                                    Vendor Assessment Tool that provides a
                                    standard questionnaire for higher education
                                    institutions to use when assessing vendor
                                    products and services.
                                </p>
                                <ul className="mt-4 list-inside list-disc">
                                    <li>
                                        <a
                                            href="https://static.getsnworks.com/downloads/2025-HECVAT402-lite.xlsx"
                                            className="text-green-700 underline"
                                        >
                                            HECVAT 4 (LITE) - Updated Apr 2025
                                        </a>{" "}
                                        (XLSX)
                                    </li>
                                    <li>
                                        <a
                                            href="https://static.getsnworks.com/downloads/2024-hecvatlite304.xlsx"
                                            className="text-green-700 underline"
                                        >
                                            HECVAT 3.04 (LITE) - Updated Feb
                                            2024
                                        </a>{" "}
                                        (XLSX)
                                    </li>
                                </ul>
                            </dd>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
